import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

const STORAGE_KEY = 'conductor.blockPreview.on';
/** Wide enough for the form and the panel side by side (task-cms-block-preview.md §5.3). */
const ON_BY_DEFAULT_FROM_PX = 1200;

/**
 * The Preview toggle (#263), shared by every editor that has the panel: on by default from 1200 px wide, then as the
 * person last left it in this browser (localStorage; a blocked store just means the default every time). When the
 * server says preview isn't set up here, the panel goes and the toggle stays off and disabled for the session.
 * `shown` is the one rule for whether a panel is mounted: every host reads it, and `changes` fires when it may change.
 */
@Injectable({ providedIn: 'root' })
export class BlockPreviewSettingsService {
  /** The person wants the panel. */
  on: boolean;
  /** False once the server said preview isn't set up on it. */
  available = true;
  /** `shown` may have changed (the toggle, the panel's Close, or the server saying it isn't set up). */
  readonly changes = new Subject<boolean>();

  constructor() {
    this.on = readStored() ?? window.innerWidth >= ON_BY_DEFAULT_FROM_PX;
  }

  /** Whether the panel is mounted: the person wants it and the server has it. */
  get shown(): boolean {
    return this.on && this.available;
  }

  set(on: boolean): void {
    this.on = on;
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      // Private window or blocked storage: the choice lasts this session only.
    }
    this.changes.next(this.shown);
  }

  /** The server answered "not configured": the panel goes, and the toggle shows off and can't be turned on. */
  notConfigured(): void {
    if (!this.available) return;
    this.available = false;
    this.changes.next(this.shown);
  }
}

function readStored(): boolean | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
}
